"use client";

import { InputBase } from "@faire/slate/private/form/InputBase";
import { Labelled } from "@faire/slate/private/form/Labelled";
import { buildFormIds } from "@faire/slate/private/form/utils";
import { PasswordModeButton } from "@faire/slate/private/password-input/PasswordModeButton";
import type { IPasswordInputWebProps } from "@faire/slate/private/password-input/types";
import type { IText } from "@faire/slate/private/shared/types";
import React, { useCallback, useState } from "react";

export type IPasswordInputBaseProps = {
  disabled?: boolean;
  error?: boolean | IText;
  helper?: IText;
  label?: string;
  optional?: boolean;
  placeholder?: string;
  tooltipText?: IText;
  value?: string;
  idPrefix: string;
  className?: string;
  "data-component-name"?: string;
} & IPasswordInputWebProps;

/**
 * The show/hide-password input composition shared by `PasswordInput` and
 * `PasswordStrengthInput`. Public components render this under their own
 * `data-component-name`/`componentCls()` root — this base carries neither, so
 * a single field never counts as two tracked component instances.
 */
export const PasswordInputBase: React.FC<IPasswordInputBaseProps> = ({
  disabled,
  error,
  helper,
  id: idProp,
  label,
  tooltipText,
  optional = false,
  autoComplete = "off",
  idPrefix,
  className,
  "data-component-name": dataComponentName,
  ...props
}) => {
  const { id, helperId } = buildFormIds({
    prefix: idPrefix,
    idProp,
    name: props.name,
    label,
    helper,
    error,
  });

  const [isPlainText, setIsPlainText] = useState(false);

  const onModeClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPlainText((v) => !v);
  }, []);

  return (
    <Labelled
      className={className}
      data-component-name={dataComponentName}
      disabled={disabled}
      error={error}
      label={label}
      inputId={id}
      helper={helper}
      helperId={helperId}
      tooltipText={tooltipText}
      optional={optional}
    >
      <InputBase
        aria-describedby={helperId}
        disabled={disabled}
        error={error}
        id={id}
        suffix={
          <PasswordModeButton isPlainText={isPlainText} onClick={onModeClick} />
        }
        type={isPlainText ? "text" : "password"}
        spellCheck={false}
        autoComplete={autoComplete}
        data-ph-no-capture
        {...props}
      />
    </Labelled>
  );
};
